import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAtom } from "jotai";
import { type JSX, useMemo, useRef } from "react";

import DotsCircle from "@repo/icons/default/DotsCircle";
import Google2 from "@repo/icons/default/Google2";
import Instagram3 from "@repo/icons/default/Instagram3";
import Linkedin3 from "@repo/icons/default/Linkedin3";
import Medical from "@repo/icons/default/Medical";
import Tiktok2 from "@repo/icons/default/Tiktok2";
import Web from "@repo/icons/default/Web";
import type { SVGIconProps } from "@repo/icons/interface";

import { Button } from "@/components/ui/button/button";
import { ProgressRoundedLayout } from "@/components/ui/layout/progress-rounded-layout";
import {
  CardSelectInputItem,
  CardSelectRoot,
} from "@/components/ui/select-input/card-select";
import { consultingAtom } from "@/lib/atoms/consulting-atom";
import { getChannelValueError } from "@/lib/form-schemas/consulting-channels-schema";

interface ChannelOption {
  id: string;
  label: string;
  Icon: (props: SVGIconProps) => JSX.Element;
  placeholder: string;
}

const CHANNELS: ChannelOption[] = [
  { id: "instagram", label: "Instagram", Icon: Instagram3, placeholder: "@seuinstagram" },
  { id: "tiktok", label: "TikTok", Icon: Tiktok2, placeholder: "@seutiktok" },
  { id: "linkedin", label: "Linkedin", Icon: Linkedin3, placeholder: "in/seu-perfil" },
  {
    id: "site",
    label: "Site ou Blog pessoal",
    Icon: Web,
    placeholder: "www.seusite.com",
  },
  {
    id: "doctoralia",
    label: "Perfil Doctoralia (ou similares)",
    Icon: Medical,
    placeholder: "doctoralia.com.br/seu-perfil",
  },
  {
    id: "google",
    label: "Google Perfil da Empresa",
    Icon: Google2,
    placeholder: "maps.app.goo.gl/sua-empresa",
  },
  {
    id: "outros",
    label: "Outros",
    Icon: DotsCircle,
    placeholder: "nomedaplataforma.com/seu-perfil",
  },
];

const ConsultingChannelsPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [form, setForm] = useAtom(consultingAtom);

  // defaultValue precisa ser estável para não resetar a seleção interna do
  // CardSelectRoot a cada render.
  const initialSelectedRef = useRef<string[]>(Object.keys(form.channels));

  const channelErrors = useMemo(() => {
    const errors: Record<string, string | undefined> = {};
    for (const id of Object.keys(form.channels)) {
      errors[id] = getChannelValueError(id, form.channels[id] ?? "");
    }
    return errors;
  }, [form.channels]);

  // Continuar exige ao menos um canal selecionado e sem erros de formato.
  // Canais selecionados sem valor são permitidos (não travam o avanço).
  const isFormValid =
    Object.keys(form.channels).length > 0 &&
    Object.keys(form.channels).every((id) => !channelErrors[id]);

  const handleSelectionChange = (selectedIds: string[]): void => {
    setForm((prev) => {
      const next: Record<string, string> = {};
      for (const id of selectedIds) {
        next[id] = prev.channels[id] ?? "";
      }
      return { ...prev, channels: next };
    });
  };

  const handleInputChange = (id: string, value: string): void => {
    setForm((prev) => ({
      ...prev,
      channels: { ...prev.channels, [id]: value },
    }));
  };

  const goToNextStep = (): void => {
    void navigate({ to: "/onboarding/consulting/career-problem" });
  };

  return (
    <ProgressRoundedLayout progress={2 / 5}>
      <div className="flex h-full flex-col justify-between gap-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <h1 className="text-xl font-medium leading-tight text-neutral-700">
              Quais destes canais você já utiliza profissionalmente?
            </h1>
            <p className="text-base font-normal text-neutral-500">
              Selecione e informe seu perfil em todos canais que você utilizar
            </p>
          </div>

          <CardSelectRoot
            name="consulting-channels"
            mode="multi"
            defaultValue={initialSelectedRef.current}
            onChange={handleSelectionChange}
            className="gap-3"
          >
            {CHANNELS.map((channel) => (
              <CardSelectInputItem
                key={channel.id}
                value={channel.id}
                icon={channel.Icon}
                placeholder={channel.placeholder}
                inputValue={form.channels[channel.id] ?? ""}
                onInputChange={(value) => handleInputChange(channel.id, value)}
                error={channelErrors[channel.id]}
              >
                {channel.label}
              </CardSelectInputItem>
            ))}
          </CardSelectRoot>
        </div>

        <div className="sticky bottom-0 flex w-full flex-col gap-3 bg-white pb-2 pt-3">
          <Button fullWidth onClick={goToNextStep} disabled={!isFormValid}>
            Continuar
          </Button>
          <Button fullWidth color="secondaryLight" onClick={goToNextStep}>
            Não utilizo nenhum canal
          </Button>
        </div>
      </div>
    </ProgressRoundedLayout>
  );
};

export const Route = createFileRoute("/onboarding/consulting/channels")({
  component: ConsultingChannelsPage,
});
